import { useEffect, useState } from "react";
import { Download, Smartphone, Loader2, Package } from "lucide-react";
import { api } from "@/lib/api";

export default function MobileAppPanel() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Hide inside the native app itself — this panel is only useful to web users.
    if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) {
      setInfo({ available: false });
      setLoading(false);
      return;
    }
    api.get("/mobile/apk-info")
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ available: false }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-6 flex items-center gap-3 text-[var(--text-secondary)]">
        <Loader2 size={18} className="animate-spin" /> Checking mobile build…
      </div>
    );
  }

  if (!info?.available) return null;

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const href = `${backend}${info.download_url}`;
  const aab = info.artifacts?.release_aab;
  const aabHref = aab ? `${backend}${aab.download_url}` : null;
  const isRelease = info.channel === "release";

  return (
    <div
      data-testid="mobile-apk-panel"
      className="rounded-xl border border-[var(--border-line)] bg-[var(--bg-surface)] p-6"
    >
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-lg bg-[#FF5A1F]/10 border border-[#FF5A1F]/30">
          <Smartphone size={22} className="text-[#FF5A1F]" />
        </div>
        <div className="flex-1">
          <p className="ft-overline mb-1">ANDROID APP</p>
          <h3 className="font-display text-2xl font-bold tracking-tight">
            Install RayFuel on Android
          </h3>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            Install the RayFuel app directly on any Android device. Same design, same features — just faster. Open this page on your phone to download and install.
          </p>
          <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--text-tertiary)]">
            <span>Package: <code className="text-[var(--text-primary)]">{info.package}</code></span>
            <span>Version: <code className="text-[var(--text-primary)]">{info.version}</code></span>
            <span>Size: <code className="text-[var(--text-primary)]">{info.size_mb} MB</code></span>
            <span>
              Channel:{" "}
              <code className={isRelease ? "text-emerald-400" : "text-yellow-400"}>
                {info.channel}
              </code>
              {isRelease ? " (signed)" : " (sideload)"}
            </span>
          </div>

          <div className="mt-5 flex flex-wrap gap-3">
            <a
              data-testid="download-apk-btn"
              href={href}
              download
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#FF5A1F] hover:bg-[#E64A0F] text-white text-sm font-semibold rounded-lg transition-colors"
            >
              <Download size={16} /> Download APK
            </a>
            {aabHref && (
              <a
                data-testid="download-aab-btn"
                href={aabHref}
                download
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-[#FF5A1F]/40 hover:border-[#FF5A1F] text-[#FF5A1F] text-sm font-semibold rounded-lg transition-colors"
                title="Upload this file to Google Play Console"
              >
                <Package size={16} /> Download AAB
                <span className="text-[10px] font-mono text-[var(--text-tertiary)]">
                  {aab.size_mb} MB
                </span>
              </a>
            )}
          </div>

          <p className="mt-3 text-xs text-[var(--text-tertiary)]">
            On your phone: enable “Install from unknown sources” for your browser when Android prompts, then open the downloaded file. The AAB is for uploading to the Google Play Console.
          </p>
        </div>
      </div>
    </div>
  );
}
